import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import moment from 'moment';
import { Accordion, Alert, Badge, Button, Card, Spinner, Table } from 'react-bootstrap';

import {
  GachaCampaignStatus,
  GachaUserBoxStateDataFragment,
  GetUserGachaBoxesQuery,
  GetUserGachaBoxesQueryVariables,
  ResetUserGachaClaimsMutation,
  ResetUserGachaClaimsMutationVariables,
  ResetUserGachaMysteryPityMutation,
  ResetUserGachaMysteryPityMutationVariables,
} from '../../../../../../__gqltypes__';
import { GET_USER_GACHA_BOXES, RESET_USER_GACHA_CLAIMS, RESET_USER_GACHA_MYSTERY_PITY } from '../../../graphql';

type Props = {
  userId: string;
};

const STATUS_VARIANT: Record<GachaCampaignStatus, string> = {
  [GachaCampaignStatus.DISABLED]: 'secondary',
  [GachaCampaignStatus.SCHEDULED]: 'info',
  [GachaCampaignStatus.RUNNING]: 'success',
  [GachaCampaignStatus.ENDED]: 'dark',
};

const formatDate = (value: unknown) => (value == null ? '—' : moment(value as number).format('YYYY-MM-DD HH:mm:ss'));

const stateKey = (state: Pick<GachaUserBoxStateDataFragment, 'campaignId' | 'boxId'>) =>
  `${state.campaignId}:${state.boxId}`;

/**
 * QA: the player's Gacha box states as the feature reads them now (refresh and recovery applied), running
 * campaigns first, with the two resets (mystery pity back to the first rate, claims of the window back to 0).
 */
export function UserGachaShopDisplay({ userId }: Props): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [message, setMessage] = useState<{ variant: string; text: string } | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery<GetUserGachaBoxesQuery, GetUserGachaBoxesQueryVariables>(
    GET_USER_GACHA_BOXES,
    { variables: { userId }, skip: !userId || !expanded, fetchPolicy: 'no-cache' }
  );
  const [resetPity] = useMutation<ResetUserGachaMysteryPityMutation, ResetUserGachaMysteryPityMutationVariables>(
    RESET_USER_GACHA_MYSTERY_PITY
  );
  const [resetClaims] = useMutation<ResetUserGachaClaimsMutation, ResetUserGachaClaimsMutationVariables>(
    RESET_USER_GACHA_CLAIMS
  );

  const states = data?.liveOps.userGachaBoxes ?? [];

  const run = (state: GachaUserBoxStateDataFragment, what: 'pity' | 'claims') => {
    const variables = { userId, campaignId: state.campaignId, boxId: state.boxId };
    setMessage(null);
    setBusyKey(`${stateKey(state)}:${what}`);
    const request: Promise<unknown> = what === 'pity' ? resetPity({ variables }) : resetClaims({ variables });
    request
      .then(() => {
        setMessage({
          variant: 'success',
          text:
            what === 'pity'
              ? `Mystery pity reset for ${state.campaignName ?? state.campaignId} / ${state.tier ?? state.boxId}.`
              : `Claims reset for ${state.campaignName ?? state.campaignId} / ${state.tier ?? state.boxId}.`,
        });
        return refetch();
      })
      .catch((err) => setMessage({ variant: 'danger', text: err.message }))
      .finally(() => setBusyKey(null));
  };

  return (
    <Card className="mb-2 shadow-sm border-0">
      <Card.Body className="py-2">
        <Accordion>
          <Accordion.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => setExpanded(!expanded)}>
            <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
              Gacha Shop boxes
              <Button variant="link">
                {!expanded && <i className="fas fa-chevron-down" />}
                {expanded && <i className="fas fa-chevron-up" />}
              </Button>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            <>
              <div className="text-muted mb-2">
                The player&apos;s state per box, as the feature reads it now (refresh and recovery applied). Reset pity
                puts the mystery chance back to the first rate; Reset claims gives the full claim limit back now.
              </div>
              {message && (
                <Alert variant={message.variant} dismissible onClose={() => setMessage(null)}>
                  {message.text}
                </Alert>
              )}
              {error && <Alert variant="danger">{error.message}</Alert>}
              {loading ? (
                <Spinner animation="border" />
              ) : states.length === 0 ? (
                <div className="text-muted">This player has no Gacha box state yet (never opened a box).</div>
              ) : (
                <Table size="sm" bordered responsive>
                  <thead>
                    <tr>
                      <th>Campaign</th>
                      <th>Box</th>
                      <th>Claims</th>
                      <th>Next refresh</th>
                      <th>Openings</th>
                      <th>Mystery pulls</th>
                      <th>Current chance</th>
                      <th>Last mystery</th>
                      <th>Updated</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {states.map((state) => {
                      const key = stateKey(state);
                      const orphaned = state.tier == null;
                      return (
                        <tr key={key}>
                          <td>
                            {state.campaignName ?? <em>deleted campaign</em>}
                            <div>
                              {state.campaignStatus && (
                                <Badge variant={STATUS_VARIANT[state.campaignStatus]}>{state.campaignStatus}</Badge>
                              )}
                            </div>
                            <div className="small text-muted">{state.campaignId}</div>
                          </td>
                          <td>
                            {orphaned ? <em>removed box</em> : state.tier}
                            <div className="small text-muted">{state.boxId}</div>
                          </td>
                          <td>
                            {state.claimsInWindow} / {state.claimLimit ?? '—'}
                          </td>
                          <td>{formatDate(state.nextRefreshAt)}</td>
                          <td>{state.openingCount}</td>
                          <td>{state.mysteryPulls}</td>
                          <td>
                            {state.currentChancePercent == null
                              ? 'no mystery'
                              : `${Math.round(state.currentChancePercent * 100) / 100}%`}
                          </td>
                          <td>{state.lastMysteryState ?? '—'}</td>
                          <td className="small">{formatDate(state.updatedAt)}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <Button
                              size="sm"
                              variant="outline-warning"
                              className="mr-1"
                              disabled={busyKey !== null}
                              onClick={() => run(state, 'pity')}
                            >
                              {busyKey === `${key}:pity` ? <Spinner animation="border" size="sm" /> : 'Reset pity'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-warning"
                              disabled={busyKey !== null}
                              onClick={() => run(state, 'claims')}
                            >
                              {busyKey === `${key}:claims` ? <Spinner animation="border" size="sm" /> : 'Reset claims'}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
              <Button size="sm" variant="link" className="p-0" onClick={() => refetch()} disabled={loading}>
                <i className="fas fa-sync mr-1" />
                Refresh
              </Button>
            </>
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>
    </Card>
  );
}
